/**
 * WebSocket Status Context
 * Provides global WebSocket connection status for UI indicators
 */

import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useChatSocket } from '@/hooks/useChatSocket';
import type { Socket } from 'socket.io-client';

interface WebSocketContextType {
  /** Socket.IO instance */
  socket: Socket | null;
  /** WebSocket connection status */
  connected: boolean;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Reconnect with fresh token */
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * WebSocket Provider
 * Manages global WebSocket connection for chat
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  // Track authentication state by watching localStorage
  const [hasToken, setHasToken] = useState(
    () => !!localStorage.getItem('access_token')
  );

  // Listen for storage changes (login/logout in other tabs or after login)
  useEffect(() => {
    const checkToken = () => {
      const tokenExists = !!localStorage.getItem('access_token');
      setHasToken(tokenExists);
    };

    // Check immediately
    checkToken();

    // Set up interval to check token periodically (every 1 second)
    // This catches token changes from login/logout
    const interval = setInterval(checkToken, 1000);

    // Also listen to storage events (for cross-tab changes)
    window.addEventListener('storage', checkToken);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkToken);
    };
  }, []);

  // Connect to chat WebSocket without handlers
  // Individual components will use this shared socket instance
  const { socket, connected, connect, disconnect, reconnect } = useChatSocket({
    enabled: hasToken, // Only enable if user is authenticated
    handlers: {}, // No global handlers - components handle their own events
  });

  return (
    <WebSocketContext.Provider
      value={{ socket, connected, connect, disconnect, reconnect }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access WebSocket connection status
 *
 * @example
 * ```typescript
 * const { connected } = useWebSocketStatus();
 *
 * return (
 *   <div>
 *     Status: {connected ? 'Connected' : 'Disconnected'}
 *   </div>
 * );
 * ```
 */
export const useWebSocketStatus = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketStatus must be used within WebSocketProvider');
  }
  return context;
};
